import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { FileUpload } from "@/components/admin/FileUpload";

interface Story {
  id: number;
  circuit_id: number;
  circuit_title?: string;
  title: string;
  title_ja: string;
  slug: string;
  excerpt: string;
  excerpt_ja: string;
  content: string;
  content_ja: string;
  image_url: string;
  category: string;
  read_time: string;
  published_date: string;
  featured: number;
  instagram_reel_url?: string;
}

interface Circuit {
  id: number;
  title: string;
}

const initialFormState = {
  circuit_id: "",
  title: "",
  title_ja: "",
  slug: "",
  excerpt: "",
  excerpt_ja: "",
  content: "",
  content_ja: "",
  image_url: "",
  category: "",
  read_time: "",
  published_date: "",
  featured: false,
  instagram_reel_url: ""
};

export default function StoriesManager() {
  const [stories, setStories] = useState<Story[]>([]);
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentStory, setCurrentStory] = useState<Story | null>(null);
  const [formData, setFormData] = useState(initialFormState);
  const { toast } = useToast();

  useEffect(() => {
    fetchStories();
    fetchCircuits();
  }, []);

  const fetchStories = () => {
    setLoading(true);
    fetchAPI("?type=stories")
      .then((data: any) => {
        setStories(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching stories:", err);
        setLoading(false);
      });
  };

  const fetchCircuits = () => {
    fetchAPI("?type=circuits")
      .then((data: any) => setCircuits(data));
  };

  const handleOpenDialog = (story?: Story) => {
    if (story) {
      setCurrentStory(story);
      setFormData({
        circuit_id: story.circuit_id.toString(),
        title: story.title,
        title_ja: story.title_ja || "",
        slug: story.slug,
        excerpt: story.excerpt,
        excerpt_ja: story.excerpt_ja || "",
        content: story.content,
        content_ja: story.content_ja || "",
        image_url: story.image_url,
        category: story.category,
        read_time: story.read_time,
        published_date: story.published_date,
        featured: story.featured == 1,
        instagram_reel_url: story.instagram_reel_url || ""
      });
    } else {
      setCurrentStory(null);
      setFormData(initialFormState);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const method = currentStory ? "PUT" : "POST";
    const endpoint = currentStory 
      ? `?type=stories&id=${currentStory.id}`
      : "?type=stories";

    try {
      const result = await fetchAPI(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (result.status === "success") {
        toast({ title: "Success", description: "Story saved successfully" });
        setIsDialogOpen(false);
        fetchStories();
      } else {
        throw new Error(result.message);
      }
    } catch (err: any) {
        toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this story?")) return;
    try {
      await fetchAPI(`?type=stories&id=${id}`, { method: "DELETE" });
      toast({ title: "Deleted", description: "Story removed" });
      fetchStories();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-serif font-bold">Stories</h1>
        <Button onClick={() => handleOpenDialog()} className="gap-2">
          <Plus className="w-4 h-4" /> Add Story
        </Button>
      </div>

      <div className="border rounded-lg shadow-sm bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Circuit</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-4">Loading...</TableCell></TableRow>
            ) : stories.map((story) => (
              <TableRow key={story.id}>
                <TableCell className="font-medium max-w-[200px] truncate">
                  <div>{story.title}</div>
                  {story.title_ja && <div className="text-xs text-muted-foreground">{story.title_ja}</div>}
                </TableCell>
                <TableCell className="max-w-[150px] truncate" title={story.slug}>{story.slug}</TableCell>
                <TableCell>{story.category}</TableCell>
                <TableCell>{story.circuit_title || "N/A"}</TableCell>
                <TableCell>{story.featured == 1 ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="outline" size="icon" onClick={() => handleOpenDialog(story)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="destructive" size="icon" onClick={() => handleDelete(story.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentStory ? "Edit Story" : "Add New Story"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title (English)</Label>
                <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Title (Japanese)</Label>
                <Input value={formData.title_ja} onChange={e => setFormData({...formData, title_ja: e.target.value})} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Slug (URL Friendly Name)</Label>
              <Input value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})} placeholder="auto-generated-from-title" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Circuit</Label>
                <Select 
                  value={formData.circuit_id} 
                  onValueChange={(val) => setFormData({...formData, circuit_id: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Circuit" />
                  </SelectTrigger>
                  <SelectContent>
                    {circuits.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
               <div className="space-y-2">
                <Label>Category</Label>
                <Input value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} placeholder="e.g. Cultural Guide" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Read Time</Label>
                    <Input value={formData.read_time} onChange={e => setFormData({...formData, read_time: e.target.value})} placeholder="8 min read" />
                </div>
                 <div className="space-y-2">
                    <Label>Published Date</Label>
                    <Input value={formData.published_date} onChange={e => setFormData({...formData, published_date: e.target.value})} placeholder="December 15, 2024" />
                </div>
            </div>

            <div className="space-y-2">
                <Label>Image</Label>
                <FileUpload 
                    value={formData.image_url} 
                    onChange={(url) => setFormData({...formData, image_url: url})} 
                    accept="image/*"
                />
            </div>

            <div className="space-y-2">
                <Label>YouTube Short URL (Optional)</Label>
                <Input 
                  value={formData.instagram_reel_url} 
                  onChange={e => setFormData({...formData, instagram_reel_url: e.target.value})} 
                  placeholder="https://youtu.be/..." 
                />
            </div>

            <div className="space-y-2">
              <Label>Excerpt (English Summary)</Label>
              <Textarea rows={2} value={formData.excerpt} onChange={e => setFormData({...formData, excerpt: e.target.value})} />
            </div>

            <div className="space-y-2">
              <Label>Excerpt (Japanese Summary)</Label>
              <Textarea rows={2} value={formData.excerpt_ja} onChange={e => setFormData({...formData, excerpt_ja: e.target.value})} />
            </div>

            <div className="space-y-2">
              <Label>Full Content (English)</Label>
              <Textarea rows={6} value={formData.content} onChange={e => setFormData({...formData, content: e.target.value})} />
            </div>

            <div className="space-y-2">
              <Label>Full Content (Japanese)</Label>
              <Textarea rows={6} value={formData.content_ja} onChange={e => setFormData({...formData, content_ja: e.target.value})} />
            </div>

            <div className="flex items-center gap-2">
              <Switch 
                checked={formData.featured} 
                onCheckedChange={(checked) => setFormData({...formData, featured: checked})} 
              />
              <Label>Featured Story</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Save Story</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
